import { useEffect, useRef } from 'react'

import { useLocales } from '../../extensions/hooks'
import { useAuthStore } from '../../features/auth/hooks'
import { api } from '../../shared/api'
import { useUiStore } from '../../shared/hooks'

/**
 * Keeps the admin language one of the site languages (`CONTENT_LOCALES`):
 * a stored language that is no longer configured falls back to English, or
 * the default site language when English is not configured (ADR-043).
 */
export function useUiLocaleSync() {
  const { locales, defaultLocale, loaded } = useLocales()
  const uiLocale = useUiStore((s) => s.uiLocale)
  const setUiLocale = useUiStore((s) => s.setUiLocale)

  useEffect(() => {
    // Only against the real list: the fallback (`['en']`) would reset every stored choice.
    if (loaded && !locales.includes(uiLocale))
      setUiLocale(locales.includes('en') ? 'en' : defaultLocale)
  }, [loaded, locales, defaultLocale, uiLocale, setUiLocale])

  useEffect(() => {
    document.documentElement.lang = uiLocale
  }, [uiLocale])

  // The server keeps the admin language for e-mails such as notifications (ADR-047).
  const signedIn = useAuthStore((s) => !!s.accessToken)
  const saved = useRef<string | null>(null)

  useEffect(() => {
    if (!signedIn || !loaded || !locales.includes(uiLocale)) return
    if (saved.current === uiLocale) return
    saved.current = uiLocale
    void api('/auth/me', { method: 'PATCH', body: { locale: uiLocale } }).catch(
      () => {
        saved.current = null
      },
    )
  }, [signedIn, loaded, locales, uiLocale])
}
